import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { TechInsight, InsightBookmark, InsightFilters, MemberInsightPreferences } from '@/types/insight';
import { useLoadingState } from './useLoadingState';

export const useInsights = () => {
  const [insights, setInsights] = useState<TechInsight[]>([]);
  const [bookmarks, setBookmarks] = useState<InsightBookmark[]>([]);
  const [preferences, setPreferences] = useState<MemberInsightPreferences | null>(null);
  const { isLoading, setLoading, executeAsync } = useLoadingState(true);
  const { toast } = useToast();

  const fetchInsights = async (filters?: InsightFilters) => {
    try {
      setLoading(true);
      let query = supabase
        .from('tech_insights')
        .select('*')
        .eq('status', 'published')
        .order('publication_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }
      if (filters?.sector && filters.sector !== 'all') {
        query = query.eq('sector', filters.sector);
      }
      if (filters?.insight_type && filters.insight_type !== 'all') {
        query = query.eq('insight_type', filters.insight_type);
      }
      if (filters?.is_featured) {
        query = query.eq('is_featured', true);
      }
      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,content.ilike.%${filters.search}%,summary.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setInsights((data || []) as TechInsight[]);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les insights",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createInsight = async (insightData: Omit<TechInsight, 'id' | 'created_at' | 'updated_at' | 'view_count' | 'bookmark_count' | 'share_count'>) => {
    try {
      // Utiliser upsert pour éviter les doublons
      const { error } = await supabase
        .from('tech_insights')
        .upsert([insightData], { onConflict: 'title', ignoreDuplicates: false });

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Insight créé avec succès",
      });

      fetchInsights();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer l'insight",
        variant: "destructive",
      });
    }
  };

  const updateInsight = async (id: string, updates: Partial<TechInsight>) => {
    try {
      const { error } = await supabase
        .from('tech_insights')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Insight mis à jour",
      });

      fetchInsights();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour l'insight",
        variant: "destructive",
      });
    }
  };

  const deleteInsight = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tech_insights')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Insight supprimé",
      });

      fetchInsights();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'insight",
        variant: "destructive",
      });
    }
  };

  // Bulk operations - throw errors to allow caller to handle selection clearing
  const bulkDeleteInsights = async (ids: string[]) => {
    const { error } = await supabase
      .from('tech_insights')
      .delete()
      .in('id', ids);

    if (error) {
      console.error('Bulk delete insights error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer les insights",
        variant: "destructive",
      });
      throw error;
    }

    toast({
      title: "Succès",
      description: `${ids.length} insight(s) supprimé(s)`,
    });
    fetchInsights();
  };

  const bulkArchiveInsights = async (ids: string[]) => {
    const { error } = await supabase
      .from('tech_insights')
      .update({ status: 'archived' })
      .in('id', ids);

    if (error) {
      console.error('Bulk archive insights error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'archiver les insights",
        variant: "destructive",
      });
      throw error;
    }

    toast({
      title: "Succès",
      description: `${ids.length} insight(s) archivé(s)`,
    });
    fetchInsights();
  };

  const bulkUpdateInsights = async (ids: string[], updates: Partial<TechInsight>) => {
    const { error } = await supabase
      .from('tech_insights')
      .update(updates)
      .in('id', ids);

    if (error) {
      console.error('Bulk update insights error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour les insights",
        variant: "destructive",
      });
      throw error;
    }

    toast({
      title: "Succès",
      description: `${ids.length} insight(s) mis à jour`,
    });
    fetchInsights();
  };

  const toggleBookmark = async (insightId: string) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('User not authenticated');

      const existingBookmark = bookmarks.find(b => b.insight_id === insightId);

      if (existingBookmark) {
        // Remove bookmark
        const { error } = await supabase
          .from('insight_bookmarks')
          .delete()
          .eq('insight_id', insightId)
          .eq('user_id', user.data.user.id);

        if (error) throw error;

        setBookmarks(bookmarks.filter(b => b.insight_id !== insightId));
        
        // Decrement bookmark count
        await supabase
          .from('tech_insights')
          .update({ 
            bookmark_count: insights.find(i => i.id === insightId)?.bookmark_count - 1 || 0 
          })
          .eq('id', insightId);
      } else {
        // Add bookmark
        const { error } = await supabase
          .from('insight_bookmarks')
          .insert([{
            insight_id: insightId,
            user_id: user.data.user.id
          }]);

        if (error) throw error;

        setBookmarks([...bookmarks, {
          id: '', // Will be filled by the response
          insight_id: insightId,
          user_id: user.data.user.id,
          created_at: new Date().toISOString()
        }]);

        // Increment bookmark count
        await supabase
          .from('tech_insights')
          .update({ 
            bookmark_count: (insights.find(i => i.id === insightId)?.bookmark_count || 0) + 1 
          })
          .eq('id', insightId);
      }

      // Update local state without refetching
      setInsights(prev => prev.map(insight => 
        insight.id === insightId 
          ? { ...insight, bookmark_count: existingBookmark 
              ? (insight.bookmark_count || 0) - 1 
              : (insight.bookmark_count || 0) + 1 }
          : insight
      ));
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de gérer le bookmark",
        variant: "destructive",
      });
    }
  };

  const trackInteraction = useCallback(async (insightId: string, interactionType: 'view' | 'share', metadata?: Record<string, any>) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) return;

      // Disabled - insight_interactions table doesn't exist
      console.log('Track insight interaction:', { insightId, interactionType, metadata });

      // Update counters locally without refetching
      if (interactionType === 'view') {
        setInsights(prev => prev.map(insight => 
          insight.id === insightId 
            ? { ...insight, view_count: (insight.view_count || 0) + 1 }
            : insight
        ));
        
        await supabase
          .from('tech_insights')
          .update({ 
            view_count: (insights.find(i => i.id === insightId)?.view_count || 0) + 1 
          })
          .eq('id', insightId);
      } else if (interactionType === 'share') {
        setInsights(prev => prev.map(insight => 
          insight.id === insightId 
            ? { ...insight, share_count: (insight.share_count || 0) + 1 }
            : insight
        ));
        
        await supabase
          .from('tech_insights')
          .update({ 
            share_count: (insights.find(i => i.id === insightId)?.share_count || 0) + 1 
          })
          .eq('id', insightId);
      }
    } catch (error: any) {
      // Silent fail for tracking
      console.error('Failed to track interaction:', error);
    }
  }, [insights]);

  const fetchUserBookmarks = async () => {
    try {
      const { data, error } = await supabase
        .from('insight_bookmarks')
        .select('id, insight_id, user_id, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookmarks((data || []) as InsightBookmark[]);
    } catch (error: any) {
      console.error('Failed to fetch bookmarks:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) return;

      // Disabled - member_insight_preferences table doesn't exist
      setPreferences({
        categories: [],
        difficulty_levels: [],
        sectors: [],
        user_id: '',
        created_at: '',
        updated_at: ''
      } as any);
    } catch (error: any) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  const updateUserPreferences = async (newPreferences: Partial<MemberInsightPreferences>) => {
    try {
      const user = await supabase.auth.getUser();
      if (!user.data.user) throw new Error('User not authenticated');

      // Disabled - member_insight_preferences table doesn't exist
      console.log('Update preferences:', newPreferences);

      toast({
        title: "Succès",
        description: "Préférences mises à jour",
      });

      fetchUserPreferences();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour les préférences",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchInsights();
    fetchUserBookmarks();
    fetchUserPreferences();
  }, []);

  return {
    insights,
    bookmarks,
    preferences,
    isLoading,
    fetchInsights,
    createInsight,
    updateInsight,
    deleteInsight,
    bulkDeleteInsights,
    bulkArchiveInsights,
    bulkUpdateInsights,
    toggleBookmark,
    trackInteraction,
    updateUserPreferences,
  };
};